// The EU spec's own §6 #3 workaround: cut a donut into half-moons so the hole's ground stays excluded.
import * as turf from '@turf/turf'
import { ringIsUndersized } from '@/utils/eudr/EudrGeometry.js'

const MAX_SPLIT_DEPTH = 8
// Total daylight between the two halves. Halves that share the cut line's exact coordinates read as one
// self-intersecting polygon to the EU validator; 2e-6° (~0.2m) stays ≥1e-6° apart even after 6dp rounding.
const CUT_GAP_DEGREES = 0.000002

// polygonCoords: [outerRing, hole1, ...]; returns hole-free polygon coordinate arrays, or null when clipping fails.
export function splitPolygonHoles (polygonCoords, depth = 0) {
  if (!polygonCoords?.length) return null
  if (polygonCoords.length === 1) return [polygonCoords]
  if (depth >= MAX_SPLIT_DEPTH) return null

  try {
    // A vertical cut through the first hole's center; remaining holes land on one side and recurse.
    const hole = polygonCoords[1]
    const cutX = hole.reduce((sum, position) => sum + position[0], 0) / hole.length
    const [minX, minY, maxX, maxY] = turf.bbox(turf.polygon([polygonCoords[0]]))
    const pad = Math.max(maxX - minX, maxY - minY, 0.001)

    // The halves clip against offset cut lines so they never share a coordinate along the cut.
    const cutLeft = cutX - CUT_GAP_DEGREES / 2
    const cutRight = cutX + CUT_GAP_DEGREES / 2
    const halfPlanes = [
      turf.polygon([[[minX - pad, minY - pad], [cutLeft, minY - pad], [cutLeft, maxY + pad], [minX - pad, maxY + pad], [minX - pad, minY - pad]]]),
      turf.polygon([[[cutRight, minY - pad], [maxX + pad, minY - pad], [maxX + pad, maxY + pad], [cutRight, maxY + pad], [cutRight, minY - pad]]])
    ]
    const subject = turf.polygon(polygonCoords)

    const pieces = []
    for (const halfPlane of halfPlanes) {
      const clipped = turf.intersect(turf.featureCollection([subject, halfPlane]))
      if (!clipped) continue
      const polygons = clipped.geometry.type === 'MultiPolygon' ? clipped.geometry.coordinates : [clipped.geometry.coordinates]
      for (const piece of polygons) {
        const resolved = splitPolygonHoles(piece, depth + 1)
        if (!resolved) return null
        pieces.push(...resolved)
      }
    }
    // Shed clip slivers once the recursion has fully unwound — unless slivers are all there is
    // (a genuinely tiny source shape), in which case dropping them would erase the feature.
    if (depth === 0 && pieces.length) {
      const kept = pieces.filter(piece => !ringIsUndersized(piece?.[0]))
      if (kept.length) return kept
    }
    return pieces.length ? pieces : null
  } catch (e) {
    return null
  }
}
